/* eslint-disable */

import React, { useEffect, useState, useMemo } from 'react'
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import { Container, ContentHeader, Title, ActionToolbar, ContentBody, SearchContainer } from './Admin.styled';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { fetchAdminRequests, setPageSize, selectPageSize, selectAdminRequestRowCount, selectShouldOpen, selectAdminRequests, selectMessage, selectSeverity, setOpen, grantAdminAccess, declineAdminAccess } from './slice';
import PageContainer from '../../components/PageContainer/PageContainer';
import { useNavigate } from 'react-router-dom';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import { AccessRequest, AdminRequest } from './types'
import styled from 'styled-components';

const AdminRequestsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 16px;
  width: 100%;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

const AdminRequestCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  background-color: #f8f9fa;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    border-color: #ff8c42;
    box-shadow: 0 4px 12px rgba(10, 30, 61, 0.1);
    transform: translateY(-2px);
  }
`;

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`;

const CardInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-family: 'Montserrat', sans-serif;
  font-size: 14px;
  color: #0a1e3d;

  div {
    margin: 0;
  }

  strong {
    color: #0a1e3d;
    font-weight: 600;
  }
`;

const CardActions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 8px;

  button {
    font-size: 12px;
    padding: 6px 12px;
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  width: 100%;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
`;

const PaginationInfo = styled.div`
  font-family: 'Montserrat', sans-serif;
  font-size: 14px;
  color: #0a1e3d;
  white-space: nowrap;
`;

const PaginationControls = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;

  button {
    font-size: 12px;
    padding: 6px 12px;
  }

  @media (max-width: 768px) {
    width: 100%;

    button {
      flex: 1;
    }
  }
`;

const PageSizeControl = styled.div`
  min-width: 150px;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const AdminRequests: React.FC = () => {
  const openServer = useAppSelector(selectShouldOpen);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const adminRequestsFromServer = useAppSelector(selectAdminRequests);
  const pageSize = useAppSelector(selectPageSize);
  const rowCount = useAppSelector(selectAdminRequestRowCount);
  const [adminRequests, setAdminRequests] = useState(adminRequestsFromServer);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(0);

  useEffect(()=>{
    setAdminRequests(adminRequestsFromServer)
  },[
    adminRequestsFromServer
  ])

  useEffect(() => {
    dispatch(fetchAdminRequests(0));
  }, [dispatch])

  useEffect(() => {
    dispatch(fetchAdminRequests(currentPage));
  }, [pageSize, dispatch, currentPage])

  const filteredRequests = useMemo(() => {
    if (!searchQuery.trim()) {
      return adminRequests;
    }
    const lowerQuery = searchQuery.toLowerCase();
    return adminRequests?.filter(request =>
      (request.name?.toLowerCase().includes(lowerQuery) ?? false) ||
      (request.email?.toLowerCase().includes(lowerQuery) ?? false) ||
      (request.type?.toLowerCase().includes(lowerQuery) ?? false) ||
      (request.status?.toLowerCase().includes(lowerQuery) ?? false)
    ) ?? [];
  }, [adminRequests, searchQuery]);

  const setOpenServer = (open: boolean) => {
    dispatch(setOpen(open));
  }

  const message = useAppSelector(selectMessage);
  const severity = useAppSelector(selectSeverity);

  const handleGrant = (request: AdminRequest) => {
    const req: AccessRequest = {
      name: request.name,
      email: request.email,
      type: request.type
    }
    dispatch(grantAdminAccess(req));
  }

  const handleDecline = (request: AdminRequest) => {
    const req: AccessRequest = {
      email: request.email
    }
    dispatch(declineAdminAccess(req));
  }

  const totalPages = Math.ceil(rowCount / pageSize);
  const startIndex = currentPage * pageSize + 1;
  const endIndex = Math.min((currentPage + 1) * pageSize, rowCount);
  const hasNextPage = currentPage < totalPages - 1;
  const hasPreviousPage = currentPage > 0;

  return (
    <PageContainer title='Admin Access Requests' open={openServer} setOpen={setOpenServer} message={message} severity={severity}>
      <Container>
        <ContentHeader>
          <Title>Admin Access Requests</Title>
        </ContentHeader>

        <SearchContainer>
          <TextField
            placeholder="Search by name, email, type, or status..."
            variant="outlined"
            size="small"
            fullWidth
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </SearchContainer>

        <ContentBody>
          <AdminRequestsGrid>
            {filteredRequests?.map((request) => (
              <AdminRequestCard key={request.adminRequestID}>
                <CardContent>
                  <CardInfo>
                    <div><strong>Name:</strong> {request.name}</div>
                    <div><strong>Email:</strong> {request.email}</div>
                    <div><strong>Type:</strong> {request.type}</div>
                    <div><strong>Status:</strong> {request.status}</div>
                    <div><strong>Created:</strong> {new Date(request.created).toLocaleDateString()}</div>
                  </CardInfo>
                </CardContent>
                <CardActions>
                  <Button
                    variant="contained"
                    size="small"
                    color="success"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleGrant(request);
                    }}
                  >
                    Grant Access
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    color="error"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDecline(request);
                    }}
                  >
                    Decline
                  </Button>
                </CardActions>
              </AdminRequestCard>
            ))}
          </AdminRequestsGrid>
        </ContentBody>

        <PaginationContainer>
          <PaginationInfo>
            Showing {rowCount > 0 ? startIndex : 0} to {endIndex} of {rowCount} entries
          </PaginationInfo>

          <PaginationControls>
            <Button
              variant="outlined"
              size="small"
              disabled={!hasPreviousPage}
              onClick={() => setCurrentPage(currentPage - 1)}
            >
              Previous
            </Button>
            <PaginationInfo>
              Page {currentPage + 1} of {totalPages}
            </PaginationInfo>
            <Button
              variant="outlined"
              size="small"
              disabled={!hasNextPage}
              onClick={() => setCurrentPage(currentPage + 1)}
            >
              Next
            </Button>
          </PaginationControls>

          <PageSizeControl>
            <FormControl size="small" fullWidth>
              <InputLabel>Page Size</InputLabel>
              <Select
                value={pageSize}
                label="Page Size"
                onChange={(e) => {
                  dispatch(setPageSize(Number(e.target.value)));
                  setCurrentPage(0);
                }}
              >
                <MenuItem value={25}>25 per page</MenuItem>
                <MenuItem value={50}>50 per page</MenuItem>
                <MenuItem value={100}>100 per page</MenuItem>
              </Select>
            </FormControl>
          </PageSizeControl>
        </PaginationContainer>
      </Container>
    </PageContainer>
  )
}

export default withAuthenticationRequired(AdminRequests)
